/**
 * DatePickerModal
 * Displays a modal with all available dates for a workshop
 */
class DatePickerModal {
  constructor() {
    // Support workshop, group, help-her-heal, and ICM page IDs
    this.modal = document.getElementById('date-picker-modal') ||
                 document.getElementById('workshop-date-picker-modal') ||
                 document.getElementById('group-date-picker-modal') ||
                 document.getElementById('hhh-date-picker-modal') ||
                 document.getElementById('icm-date-picker-modal');
    this.modalBackdrop = document.querySelector('.modal-backdrop');
    this.modalCloseButtons = document.querySelectorAll('.modal-close, .modal-close-btn');
    this.datesList = document.getElementById('dates-list') ||
                     document.getElementById('workshop-dates-list') ||
                     document.getElementById('group-dates-list') ||
                     document.getElementById('hhh-dates-list') ||
                     document.getElementById('icm-dates-list');
    this.modalTitle = document.getElementById('modal-workshop-title') ||
                      document.getElementById('workshop-modal-title') ||
                      document.getElementById('group-modal-title') ||
                      document.getElementById('hhh-modal-title') ||
                      document.getElementById('icm-modal-title');
    this.workshopData = this.loadWorkshopData();

    this.init();
  }

  init() {
    // Attach listeners to date picker buttons
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('date-picker-btn')) {
        // Support data-workshop-id, data-group-id, data-hhh-id, and data-icm-id
        const workshopId = e.target.getAttribute('data-workshop-id') ||
                          e.target.getAttribute('data-group-id') ||
                          e.target.getAttribute('data-hhh-id') ||
                          e.target.getAttribute('data-icm-id');
        this.show(workshopId);
      }
    });

    // Attach close button listeners
    this.modalCloseButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Close modal on backdrop click
    if (this.modalBackdrop) {
      this.modalBackdrop.addEventListener('click', () => this.close());
    }

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.style.display !== 'none') {
        this.close();
      }
    });

    console.log('✅ DatePickerModal initialized');
  }

  loadWorkshopData() {
    // Support workshop-data, group-data, hhh-data, and icm-data elements
    const dataElement = document.getElementById('workshop-data') ||
                        document.getElementById('group-data') ||
                        document.getElementById('hhh-data') ||
                        document.getElementById('icm-data');
    if (!dataElement) {
      console.warn('⚠️ Data element not found (workshop-data, group-data, hhh-data, or icm-data)');
      return [];
    }

    try {
      const data = JSON.parse(dataElement.textContent);
      console.log('📋 Loaded event data:', data);
      return data;
    } catch (error) {
      console.error('❌ Error parsing event data:', error);
      return [];
    }
  }

  show(workshopId) {
    // Parse workshopId (format: "groupIndex-eventIndex")
    const [groupIndex, eventIndex] = workshopId.split('-').map(Number);

    console.log('🔍 DatePickerModal.show called:', { workshopId, groupIndex, eventIndex });
    console.log('🔍 Available workshopData:', this.workshopData);

    if (isNaN(groupIndex) || isNaN(eventIndex)) {
      console.error('❌ Invalid workshop ID:', workshopId);
      return;
    }

    const workshop = this.workshopData[groupIndex];
    console.log('🔍 Selected workshop:', workshop);

    const event = workshop?.events[eventIndex];
    console.log('🔍 Selected event:', event);

    if (!workshop || !event) {
      console.error('❌ Workshop or event not found:', { groupIndex, eventIndex, workshop, event });
      console.error('❌ Available workshopData length:', this.workshopData.length);
      return;
    }

    // Update modal title
    if (this.modalTitle) {
      this.modalTitle.textContent = `${workshop.title} - All Dates`;
    }

    // Build dates list
    this.buildDatesList(event);

    // Show modal
    if (this.modal) {
      this.modal.style.display = 'flex';
      console.log('📅 Modal opened for workshop:', workshop.title);
    }
  }

  buildDatesList(event) {
    if (!this.datesList) return;

    // Clear existing content
    this.datesList.innerHTML = '';

    const dates = event.consolidatedDates || [];

    if (dates.length === 0) {
      this.datesList.innerHTML = '<p>No dates available</p>';
      return;
    }

    // Create date items — pin to America/Chicago so dates match the event's
    // actual calendar day regardless of the visitor's local timezone.
    const TZ = 'America/Chicago';
    const dateItems = dates.map(date => {
      const startDate = new Date(date.start.dateTime || date.start.date);
      const endDate = new Date(date.end.dateTime || date.end.date);
      const isAllDay = !date.start.dateTime;

      const dateStr = startDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: TZ
      });

      let timeStr = 'All Day';
      if (!isAllDay) {
        const tzFmt = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: TZ };
        const startTime = startDate.toLocaleTimeString('en-US', tzFmt);
        const endTime = endDate.toLocaleTimeString('en-US', tzFmt);
        // Derive the correct abbreviation (CST in winter, CDT in summer)
        const tzAbbr = new Intl.DateTimeFormat('en-US', { timeZone: TZ, timeZoneName: 'short' })
          .formatToParts(startDate).find(p => p.type === 'timeZoneName')?.value || 'CT';
        timeStr = `${startTime} - ${endTime} ${tzAbbr}`;
      }

      return `
        <div class="date-item">
          <div class="date-item-date">${dateStr}</div>
          <div class="date-item-time">${timeStr}</div>
        </div>
      `;
    }).join('');

    this.datesList.innerHTML = dateItems;
  }

  close() {
    if (this.modal) {
      this.modal.style.display = 'none';
      console.log('❌ Modal closed');
    }
  }
}

export default DatePickerModal;
